// clearance-tier-pricing
//
// Prices every clearance PDP surface — the PDP, the quick add modal, and
// menu-with-pdp — so all of them agree with the Clearance V3 grid and with each
// other. The percentage comes from resolveClearanceDiscount() in
// snippets/global-js.liquid, the single client-side resolver.
//
// In tag mode the discount is fixed by the product's own tags, so every
// quantity option shows the same percentage and the cart count is irrelevant.
// In quantity mode the price a shopper sees is the tier they unlock by adding
// this item, matching updateSinglePricing in clearance-v3.js.
//
// Liquid renders the same prices server-side (snippets/pdp-product-price.liquid
// and snippets/pdp-clearance-quantity-selector.liquid, both via
// snippets/clearance-tier-discount.liquid) so the first paint is already correct.
// This file keeps them correct afterwards, when the cart or the selected product
// changes.

// Present only on the Clearance flow pages (<clearance-v3-flow> and the V4
// fork <sale-page-flow>), where it owns the merchant-configured tiers and its
// own cart count (which also counts clearance-tagged variants added without the
// line item property).
function getClearanceV3Flow() {
  const flow = document.querySelector('clearance-v3-flow, sale-page-flow')
  if (!flow || typeof flow.getDiscountForCount !== 'function') return null
  return flow
}

// The cart drawer embeds the cart as JSON, so the count needs no fetch.
function getEmbeddedCartJson() {
  const cartJson = document.querySelector('[data-cart-json]')
  if (!cartJson) return null
  try {
    return JSON.parse(cartJson.textContent)
  } catch (error) {
    console.warn('clearance-tier-pricing - unable to parse embedded cart json', error)
    return null
  }
}

function countCartClearanceItems() {
  const cart = getEmbeddedCartJson()
  if (!Array.isArray(cart?.items)) return 0
  return cart.items.reduce(function (total, item) {
    if (item.properties?._clearance !== 'true') return total
    return total + Number(item.quantity || 0)
  }, 0)
}

function getClearanceCartCount() {
  // Tag mode prices from the product's tags alone, so skip the cart entirely.
  if (isClearanceTagMode()) return 0
  const flow = getClearanceV3Flow()
  if (flow) return Number(flow.clearanceCount || 0)
  return countCartClearanceItems()
}

function hasClearanceSelectTag(productInfo) {
  return productInfo?.dataset.clearanceSelectTagged === 'true'
}

function hasClearanceBaseTag(productInfo) {
  return productInfo?.dataset.clearanceBaseTagged === 'true'
}

// Returns 0 when the product earns no clearance discount, which in tag mode
// means it carries neither discount tag.
function getClearanceDiscountForCount(count, productInfo) {
  if (!isClearanceTagMode()) {
    const flow = getClearanceV3Flow()
    if (flow) return Number(flow.getDiscountForCount(count) || 0)
  }
  return resolveClearanceDiscount({
    hasSelectTag: hasClearanceSelectTag(productInfo),
    hasBaseTag: hasClearanceBaseTag(productInfo),
    isClearance: productInfo?.dataset.productClearance === 'true',
    count: count,
  })
}

// Adding `quantity` of this item puts the shopper at cart count + quantity.
function getClearanceDiscountForAddedQuantity(quantity, productInfo) {
  return getClearanceDiscountForCount(
    getClearanceCartCount() + Number(quantity || 1),
    productInfo,
  )
}

// Mirrors Shopify's per-unit "each" allocation: the discount is truncated to the
// cent, then subtracted. Keeps displayed prices matching the cart.
function getClearanceDiscountedPriceCents(priceCents, percentage) {
  return priceCents - Math.floor((priceCents * Number(percentage || 0)) / 100)
}

// True when this product-info earns a clearance discount worth writing prices
// for. Quantity tiers only ever apply to clearance singles — packs are priced
// as a unit — but a tag-mode discount applies at any pack size.
function isClearancePricedProductInfo(productInfo) {
  if (!(getClearanceDiscountForAddedQuantity(1, productInfo) > 0)) return false
  if (isClearanceTagMode()) return true
  const packSize = productInfo.dataset.productPackSize
  return !packSize || packSize === 'Single'
}

// The grid discounts the variant price, while the PDP's own clearance pricing
// starts from compare-at whenever that is higher — follow the grid.
function getClearanceBasePriceCents(productInfo) {
  return Math.round(Number(productInfo.dataset.productPrice || 0) * 100)
}

function formatClearancePrice(productInfo, cents) {
  if (typeof productInfo.formatProductMoneyWithoutTrailingZeros === 'function')
    return productInfo.formatProductMoneyWithoutTrailingZeros(cents)

  const flow = getClearanceV3Flow()
  if (flow && typeof flow.formatMoney === 'function') return flow.formatMoney(cents)
  return ''
}

function repriceClearanceQuantityOption(input, context) {
  const { productInfo, basePriceCents, priceTemplate, quantityLabel, saveLabelTemplate } = context
  const discount = getClearanceDiscountForAddedQuantity(input.value, productInfo)
  const itemPriceCents = getClearanceDiscountedPriceCents(basePriceCents, discount)
  const formattedPrice = formatClearancePrice(productInfo, itemPriceCents)
  if (!formattedPrice) return

  const priceText = priceTemplate.replace('__PRICE__', formattedPrice)
  const saveLabel = discount > 0 ? saveLabelTemplate.replace('__PCT__', discount) : ''

  // liquid-pdp.js recomputes these prices from data-discount-percentage on later
  // variant changes, so the tier percentage has to be written back too.
  input.dataset.discountPercentage = String(discount)
  input.dataset.clearanceItemPrice = String(itemPriceCents)
  input.dataset.clearanceFormattedPrice = formattedPrice
  input.dataset.clearanceFinalPriceValue = formattedPrice
  input.dataset.clearanceFinalPriceSuffix =
    input.value === '1' ? '' : priceText.replace(formattedPrice, '')
  input.dataset.saveLabel = saveLabel
  input.setAttribute(
    'aria-label',
    `${quantityLabel} ${input.value}. ${priceText}${saveLabel ? `. ${saveLabel}` : ''}`,
  )

  const option = input.nextElementSibling
  const priceNode = option?.querySelector('[data-clearance-quantity-price]')
  const discountNode = option?.querySelector('.pdp-clearance-quantity__discount')
  if (priceNode) priceNode.textContent = priceText
  if (discountNode) discountNode.textContent = saveLabel
}

// Used when the quantity selector is absent: price a single item.
function repriceClearanceFinalPrice(productInfo, basePriceCents) {
  const finalPriceValue = productInfo.querySelector('[data-clearance-final-price-value]')
  if (!finalPriceValue) return

  const discount = getClearanceDiscountForAddedQuantity(1, productInfo)
  const formattedPrice = formatClearancePrice(
    productInfo,
    getClearanceDiscountedPriceCents(basePriceCents, discount),
  )
  if (!formattedPrice) return

  finalPriceValue.textContent = formattedPrice
  const finalPriceSuffix = productInfo.querySelector('[data-clearance-final-price-suffix]')
  if (finalPriceSuffix) finalPriceSuffix.textContent = ''
}

function applyClearanceTierPricingTo(productInfo) {
  if (!isClearancePricedProductInfo(productInfo)) return

  const basePriceCents = getClearanceBasePriceCents(productInfo)
  if (!basePriceCents) return

  const selector = productInfo.querySelector('[data-clearance-quantity-selector]')
  const inputs = selector ? selector.querySelectorAll('[data-clearance-quantity-input]') : []
  if (!inputs.length) return repriceClearanceFinalPrice(productInfo, basePriceCents)

  const context = {
    productInfo: productInfo,
    basePriceCents: basePriceCents,
    priceTemplate: selector.dataset.priceTemplate || '__PRICE__',
    quantityLabel: selector.dataset.quantityLabel || '',
    saveLabelTemplate: selector.dataset.saveLabelTemplate || '',
  }
  inputs.forEach((input) => repriceClearanceQuantityOption(input, context))

  // Mirrors the checked option into the large price row.
  if (typeof productInfo.updateSelectedClearanceFinalPrice === 'function') {
    productInfo.updateSelectedClearanceFinalPrice()
  } else {
    repriceClearanceFinalPrice(productInfo, basePriceCents)
  }
}

function applyClearanceTierPricing() {
  document.querySelectorAll('product-info').forEach(applyClearanceTierPricingTo)
}

// Deferred so it lands after liquid-pdp.js has written its own prices.
function scheduleClearanceTierPricing() {
  window.setTimeout(applyClearanceTierPricing, 0)
}

document.addEventListener('DOMContentLoaded', scheduleClearanceTierPricing)
// Quick add modal finished injecting the PDP markup.
document.addEventListener('quick-modal-content-updated', scheduleClearanceTierPricing)
// Colour, size, or pack switch re-rendered the PDP markup.
document.addEventListener('product-info-updated', scheduleClearanceTierPricing)
// Cart changed, so the unlocked tier may have too. The embedded cart JSON is
// re-rendered with the drawer, hence the extra tick before re-reading it.
document.addEventListener('cartupdatesuccess', scheduleClearanceTierPricing)
document.addEventListener('cartaddsuccess', scheduleClearanceTierPricing)
document.addEventListener('clearanceatcsuccess', scheduleClearanceTierPricing)
document.addEventListener('change', (event) => {
  if (!event.target.closest?.('product-info')) return
  scheduleClearanceTierPricing()
})
